import React, { useMemo } from 'react'

import { ReceiptTextIcon } from 'lucide-react'
import { relativeOrDateTime, toDollar } from 'utils'
import { EmptyState, Table, StatusChipCell, CategoryCell } from 'components'
import { INVOICE_STATUSES } from 'constants/statuses'
import type { CompanyInvoice } from 'types'

type CompanyInvoicesPanelProps = {
  invoices?: CompanyInvoice[]
}

export const CompanyInvoicesPanel = ({
  invoices = [],
}: CompanyInvoicesPanelProps) => {
  const rows = useMemo(
    () =>
      invoices.map((invoice) => ({
        id: invoice.id,
        number: invoice.number,
        status: invoice.status ?? 'open',
        total: invoice.total ?? invoice.amount_due ?? 0,
        type: invoice.type ?? 'other',
        created_at: invoice.created_at,
        due_date: invoice.due_date,
      })),
    [invoices]
  )

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'Invoice',
        width: 180,
        type: 'string',
        renderCell: ({ row }: { row: { id: string; number: string | null } }) =>
          row.number ? `#${row.number}` : row.id,
      },
      {
        field: 'created_at',
        headerName: 'Date',
        type: 'string',
        width: 200,
        renderCell: ({ value }: { value: string | null | undefined }) =>
          value ? relativeOrDateTime(value) : '—',
      },
      {
        field: 'due_date',
        headerName: 'Due date',
        type: 'string',
        width: 200,
        renderCell: ({ value }: { value: string | null | undefined }) =>
          value ? relativeOrDateTime(value) : '—',
      },
      {
        field: 'total',
        headerName: 'Total',
        type: 'number',
        width: 120,
        renderCell: ({ value }: { value: number }) => toDollar(value ?? 0, true),
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'string',
        width: 160,
        renderCell: ({ row }: { row: { type: string } }) => (
          <CategoryCell category={row.type ?? 'other'} filter align="start" />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 120,
        renderCell: ({ value }: { value: string }) => (
          <StatusChipCell
            status={value ?? 'open'}
            statuses={INVOICE_STATUSES}
          />
        ),
      },
    ],
    []
  )

  return (
    <Table
      fillBottom
      loading={false}
      topOffset={360}
      columns={columns}
      rows={rows}
      emptyState={
        <EmptyState
          title="No invoices"
          text="This company has no invoices yet."
          icon={<ReceiptTextIcon strokeWidth={1} size={32} />}
        />
      }
    />
  )
}
